import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { faUser, faTruck, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { toast } from 'react-toastify';
import GenericModal from '../../components/agv-generic-modal';
import Row from '../../components/agv-generic-table/row';
import InputCombobox from '../../components/agv-forms-combobox/input-combobox';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetResponsiblesPreview from '../../api/responsibles/useGetResponsiblesPreview';
import useSendNotifications from '../../api/responsibles/useSendNotifications';

export default function ResponsiblesNotificationModal({ show, onHide, date }) {
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [sendError, setSendError] = useState(null);

  const { previewData, isLoading, refetch } = useGetResponsiblesPreview(
    show ? date : null
  );
  const { sendNotifications, isLoading: isSending } = useSendNotifications();

  useEffect(() => {
    if (show && date) {
      refetch();
    }
  }, [show, date, refetch]);

  useEffect(() => {
    if (!show) {
      setSelectedResponsibles([]);
      setActiveTab(0);
      setSendError(null);
    }
  }, [show]);

  const handleChangeSelected = (selected) => {
    if (selected.includes('ALL')) {
      const allIds = previewData.responsibles.map((r) => r.id);
      setSelectedResponsibles(allIds);
    } else {
      setSelectedResponsibles(selected);
    }
  };

  const handleSendNotifications = async () => {
    if (selectedResponsibles.length === 0) {
      setSendError('Veuillez sélectionner au moins un commercial');
      return;
    }

    setSendError(null);

    try {
      const result = await sendNotifications(date, selectedResponsibles);
      const successMessage =
        result.sent_count > 1
          ? `Notifications envoyées avec succès à ${result.sent_count} commerciaux`
          : `Notification envoyée avec succès à 1 commercial`;

      toast.success(successMessage);
      onHide();
    } catch (error) {
      console.error("Erreur lors de l'envoi des notifications:", error);
      setSendError(error.message || "Erreur lors de l'envoi des notifications");
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <SkeletonLoader height={60} width="100%" />
          <SkeletonLoader height={60} width="100%" />
          <SkeletonLoader height={60} width="100%" />
        </>
      );
    }

    if (!previewData) {
      return (
        <div className="tw-text-center tw-text-dimgray-100">
          Aucune donnée disponible
        </div>
      );
    }

    if (!previewData.can_send && previewData.blocking_reason) {
      return (
        <div className="tw-text-center">
          <div className="tw-text-base tw-font-semibold tw-text-red-600 tw-mb-2">
            Envoi impossible
          </div>
          <div className="tw-text-sm tw-text-dimgray-100">
            {previewData.blocking_reason}
          </div>
        </div>
      );
    }

    if (!previewData.responsibles || previewData.responsibles.length === 0) {
      return (
        <div className="tw-text-center">
          <div className="tw-text-base tw-font-semibold tw-text-dimgray-100 tw-mb-2">
            Aucun commercial trouvé
          </div>
          <div className="tw-text-sm tw-text-dimgray-100">
            Aucun commercial associé aux tournées du{' '}
            {moment(date).format('DD/MM/YYYY')}
          </div>
        </div>
      );
    }

    const nbCommercials = previewData.responsibles.length;

    return (
      <>
        {/* Message d'erreur d'envoi */}
        {sendError && (
          <div className="tw-mb-4 tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md">
            <div className="tw-text-sm tw-text-red-800">
              <span className="tw-font-semibold">Erreur : </span>
              {sendError}
            </div>
          </div>
        )}

        {/* Sélecteur multiple */}
        <InputCombobox
          value={selectedResponsibles}
          setValue={handleChangeSelected}
          placeholder="Sélectionnez les commerciaux à notifier"
          fieldTitle="Commerciaux"
          items={[
            {
              name: 'Sélectionner tous les commerciaux',
              value: 'ALL',
            },
            ...previewData.responsibles.map((responsible) => ({
              name: responsible.factory_name
                ? `${responsible.name} (${responsible.factory_name})`
                : responsible.name,
              value: responsible.id,
            })),
          ]}
          isRequired
          emptyError="Veuillez sélectionner au moins un commercial"
          style="tw-mt-4"
          isMultiple
        />

        {/* Onglets pour les détails - Toujours affichés */}
        <div className="tw-mt-6">
          <div className="tw-text-smi tw-font-work tw-text-dimgray-100 tw-mb-3">
            Récapitulatif de l'envoi
            {selectedResponsibles.length > 0 && (
              <span>
                {' '}
                ({selectedResponsibles.length}{' '}
                {selectedResponsibles.length > 1
                  ? 'commerciaux sélectionnés'
                  : 'commercial sélectionné'}
                )
              </span>
            )}
          </div>

          <TabGroup onChange={setActiveTab} defaultIndex={0}>
            <TabList className="tw-w-full tw-rounded-md tw-py-2 tw-px-2 tw-bg-slategray-default tw-border tw-border-solid tw-space-x-4 tw-border-[#dfe3e6]">
              {previewData.responsibles.map((responsible) => (
                <Tab
                  key={responsible.id}
                  className="tw-rounded-md tw-border-0 tw-text-sm tw-py-1.5 tw-px-3 tw-bg-transparent data-[selected]:tw-bg-main data-[selected]:tw-text-white"
                >
                  {responsible.name}
                </Tab>
              ))}
            </TabList>

            <TabPanels className="tw-mt-4">
              {previewData.responsibles.map((responsible) => {
                const nbClients = responsible.total_clients;
                const nbTours = responsible.tours.length;
                const isSelected = selectedResponsibles.includes(
                  responsible.id
                );

                return (
                  <TabPanel key={responsible.id}>
                    {/* Info commercial */}
                    <Row
                      title={responsible.name}
                      subtitle={responsible.email || "Pas d'email"}
                      icon={faUser}
                      iconColor="tw-text-checklist-common"
                      style="tw-px-0 tw-border-solid tw-border-0 tw-border-b tw-border-background tw-py-4"
                    />

                    {/* Usine associée */}
                    {responsible.factory_name && (
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-mb-1 tw-text-sm tw-text-dimgray-100">
                        <FontAwesomeIcon
                          icon={faBuilding}
                          className="tw-text-xs tw-text-checklist-common"
                        />
                        <span>{responsible.factory_name}</span>
                      </div>
                    )}

                    <div className="tw-text-sm tw-text-dimgray-100 tw-mt-3 tw-mb-4">
                      {isSelected ? (
                        <span className="tw-text-checklist-common tw-font-semibold">
                          ✓ Sélectionné -{' '}
                        </span>
                      ) : (
                        <span className="tw-text-dimgray-100">
                          Non sélectionné -{' '}
                        </span>
                      )}
                      {nbClients} {nbClients > 1 ? 'clients' : 'client'} sur{' '}
                      {nbTours} {nbTours > 1 ? 'tournées' : 'tournée'}
                    </div>

                    {/* Tournées */}
                    <div className="tw-text-smi tw-font-work tw-text-dimgray-100 tw-mb-2">
                      Tournées
                    </div>

                    {responsible.tours.map((tour) => (
                      <div key={tour.tour_id} className="tw-mb-4">
                        <Row
                          title={tour.tour_key}
                          subtitle={`Véhicule ${tour.vehicule_key}${
                            tour.vehicle_label ? ` (${tour.vehicle_label})` : ''
                          } • Chauffeur ${tour.driver_fullname || 'NC'}${
                            tour.coltineur_fullname
                              ? ` • Coltineur ${tour.coltineur_fullname}`
                              : ''
                          }`}
                          icon={faTruck}
                          iconColor="tw-text-checklist-common"
                          style="tw-px-0 tw-border-solid tw-border-0 tw-border-t tw-border-background tw-py-2"
                        />

                        {/* Liste des clients */}
                        {tour.clients && tour.clients.length > 0 && (
                          <div className="tw-ml-6 tw-mt-2 tw-mb-3">
                            <div className="tw-text-xs tw-font-work tw-text-dimgray-100 tw-mb-1">
                              {tour.clients.length}{' '}
                              {tour.clients.length > 1 ? 'clients' : 'client'} :
                            </div>
                            <div className="tw-text-xs tw-text-text-dark-medium-importance">
                              {tour.clients
                                .sort((a, b) => a.order - b.order)
                                .map((client, idx) => (
                                  <span key={idx}>
                                    {idx > 0 && ' • '}
                                    {client.order}. {client.name}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </TabPanel>
                );
              })}
            </TabPanels>
          </TabGroup>
        </div>
      </>
    );
  };

  const canSend =
    previewData?.can_send &&
    previewData?.responsibles?.length > 0 &&
    selectedResponsibles.length > 0 &&
    !isSending;

  const nbSelected = selectedResponsibles.length;
  const buttonText = isSending
    ? 'Envoi en cours...'
    : nbSelected > 1
      ? `Envoyer à ${nbSelected} commerciaux`
      : nbSelected === 1
        ? 'Envoyer à 1 commercial'
        : 'Envoyer';

  const showSendButton =
    previewData?.can_send && previewData?.responsibles?.length > 0;

  return (
    <GenericModal
      isOpen={show}
      setIsOpen={onHide}
      title={`Notification aux commerciaux - ${moment(date).format('DD/MM/YYYY')}`}
      mainAction={showSendButton ? handleSendNotifications : null}
      mainActionText={buttonText}
      mainActionDisabled={!canSend}
      mainActionIsLoading={isSending}
      secondaryAction={onHide}
      secondaryActionText="Annuler"
      size="tw-max-w-[900px]"
    >
      {renderContent()}
    </GenericModal>
  );
}
